import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DynamicInput, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useIntegration, { useIntegrationForm } from '@hooks/useIntegration'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { IntegrationPlatformField } from '@functions/integrationPlatform'

import { confirmDelete } from '@functions/entity'
import integrationPlatforms from './integrationPlatforms'

import IntegrationPlatform from './components/IntegrationPlatform'

const MODAL_KEY = 'CreateOrEditIntegrationModal'

const customRequiredFields = [
  { key: 'data.api_key' },
]

type ModalCallbacks = {
  closeModal: () => void,
  closeAllModals: () => void,
  createIntegration: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditIntegrationModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeAllModals, createIntegration } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { integration: initIntegration } = modalPayload

  const newRecord = !initIntegration.id

  const {
    callbacks: { createOrUpdateIntegration: createOrEditFn, deleteIntegration: deleteFn },
    creating,
    deleting,
    integration,
    integration: {
      service_provider,
    },
    updating,
  } = useIntegration(initIntegration)

  const targetIntegrationPlatform = integrationPlatforms.find(item => item.key === service_provider)

  const integrationFormPayload = useIntegrationForm(integration, {
    customRequiredFields,
    validateOn: 'change',
  })

  if (targetIntegrationPlatform === undefined){
    return null
  }

  const integrationPlatformFields = targetIntegrationPlatform.data.fields

  const IntegrationPlatformComponent = targetIntegrationPlatform.data.component

  const {
    entityState,
    entityState: {
      data,
    },
    handlers,
    saveEnabled,
  } = integrationFormPayload

  const createIntegrationPayload = {
    callbacks: {
      action: createOrEditFn,
      afterAction: closeAllModals,
    },
    entityParams: entityState,
    toastText: `Integration ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeAllModals,
      deleteFn,
    },
    strings: {
      entityName: 'Integration',
      deleteString: 'Delete',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Integration"
      />

      <SidebarModal.SubHeader>
        <IntegrationPlatform integrationPlatform={targetIntegrationPlatform} />
      </SidebarModal.SubHeader>

      <SidebarModal.Content>
        { IntegrationPlatformComponent && <IntegrationPlatformComponent /> }
        <Form>
          {integrationPlatformFields.map((field: IntegrationPlatformField) => {
            const {
              key, label, option_values, required,
            } = field

            return (
              <Form.Field
                direction="column"
                key={key}
                label={required ? `* ${label}` : label}
              >
                <DynamicInput
                  currentValue={data[key] || ''}
                  inputComponent={option_values ? 'select' : 'input'}
                  name={`data.${key}`}
                  required={required}
                  // @ts-expect-error TS2322
                  values={option_values && option_values.map(item => ({ key: item.key, label: item.value }))}
                  {...handlers}
                />
              </Form.Field>
            )
          })}
        </Form>

        {!newRecord && (
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            loading={deleting}
            // @ts-expect-error TS2322
            marginTop="medium"
            // @ts-expect-error TS2345
            onClick={() => confirmDelete(confirmDeletePayload)}
            size="medium"
          >
            Delete Integration
          </Button>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          // @ts-expect-error TS2345
          onClick={() => createIntegration(createIntegrationPayload)}
          size="large"
        >
          {newRecord ? 'Create Integration' : 'Update Integration'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditIntegrationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
